// ./src/components/generic/AppButton/AppButton.tsx
import React from "react";
import { Button, ButtonProps, Tooltip } from "@mantine/core";
import { AppIcon } from "../AppIcon";
import { LoadingIndicator } from "../LoadingIndicator";

export interface AppButtonProps
  extends Omit<
    ButtonProps,
    "children" | "leftSection" | "rightSection" | "variant" | "color"
  > {
  /** Button text content */
  children?: React.ReactNode;

  /** Alternative to children for button text */
  label?: string;

  /** Icon name (without 'Icon' prefix) to display */
  iconName?: string | undefined;

  /** Loading state - disables button and shows loading indicator */
  inProgress?: boolean;

  /** Button variant by purpose */
  variant?: "primary" | "secondary" | "tertiary" | "danger";

  /** Icon position when both text and icon are present */
  iconPosition?: "left" | "right";

  /** Size of the icon */
  iconSize?: number;

  type?: "button" | "submit" | "reset" | undefined;

  /** Click handler */
  onClick?: (() => void) | undefined;

  /** Tooltip text to display on hover */
  tooltip?: string;

  /** Tooltip position */
  tooltipPosition?: "top" | "right" | "bottom" | "left";
}

export const AppButton: React.FC<AppButtonProps> = ({
  children,
  label,
  iconName,
  size = "md",
  inProgress = false,
  variant = "primary",
  iconPosition = "left",
  iconSize,
  disabled,
  onClick,
  type,
  tooltip,
  tooltipPosition = "top",
  ...buttonProps
}) => {
  const buttonText = children || label;
  const isDisabled = disabled || inProgress;

  // Map app variants to Mantine variants
  const getMantineVariant = (): NonNullable<ButtonProps["variant"]> => {
    switch (variant) {
      case "primary":
        return "filled";
      case "secondary":
        return "outline";
      case "tertiary":
        return "subtle";
      case "danger":
        return "filled";
      default:
        return "filled";
    }
  };

  // Get button color based on variant
  const getButtonColor = (): string => {
    switch (variant) {
      case "danger":
        return "red";
      default:
        return "blue";
    }
  };

  // Determine icon size based on button size
  const getIconSize = (): number => {
    if (iconSize) return iconSize;

    switch (size) {
      case "xs":
        return 12;
      case "sm":
        return 14;
      case "md":
        return 16;
      case "lg":
        return 18;
      case "xl":
        return 20;
      default:
        return 16;
    }
  };

  // Determine if this is an icon-only button
  const isIconOnly = iconName && !buttonText;

  // Get the icon or loading indicator to show
  const getIconElement = () => {
    if (inProgress) {
      return <LoadingIndicator size={getIconSize()} />;
    }

    if (iconName) {
      return <AppIcon iconName={iconName} size={getIconSize()} />;
    }

    return null;
  };

  const mantineVariant = getMantineVariant();
  const buttonColor = getButtonColor();

  // Common button props
  const commonProps = {
    size,
    variant: mantineVariant,
    color: buttonColor,
    disabled: isDisabled,
    type,
    onClick,
    ...buttonProps,
  };

  // Render button content
  const renderButton = () => {
    // For icon-only buttons
    if (isIconOnly) {
      return <Button {...commonProps}>{getIconElement()}</Button>;
    }

    // For text-only or text+icon buttons
    const iconElement = getIconElement();
    const leftSection = iconPosition === "left" ? iconElement : undefined;
    const rightSection = iconPosition === "right" ? iconElement : undefined;

    return (
      <Button
        {...commonProps}
        {...(leftSection && { leftSection })}
        {...(rightSection && { rightSection })}
      >
        {buttonText}
      </Button>
    );
  };

  // Wrap with tooltip if tooltip text is provided
  if (tooltip) {
    return (
      <Tooltip
        label={tooltip}
        position={tooltipPosition}
        withArrow
        openDelay={500}
      >
        {renderButton()}
      </Tooltip>
    );
  }

  return renderButton();
};
